import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldAlt, faUsers, faSearch, faTrash, faUserSecret,
  faCheckCircle, faTimesCircle, faCrown, faSpinner, faChartBar,
  faExclamationTriangle, faUserCheck, faArrowLeft, faEye,
  faTimes, faServer, faDatabase, faUsersCog, faSignOutAlt,
  faLock,
} from '@fortawesome/free-solid-svg-icons';
import { RootState } from '../store';
import { setCredentials, logout } from '../store/slices/authSlice';
import { superAdminAPI } from '../services/api';
import '../styles/SuperAdmin.css';

interface PlatformUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  platformRole: string;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  createdAt: string;
  lastLogin: string | null;
  _count: { items: number; workspaceMembers: number };
}

interface PlatformStats {
  totalUsers: number;
  verifiedUsers: number;
  unverifiedUsers: number;
  totalWorkspaces: number;
  totalItems: number;
  recentSignups: number;
  activeToday: number;
}

interface UserDetail {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  bio: string | null;
  platformRole: string;
  emailVerified: boolean;
  twoFactorEnabled: boolean;
  defaultCurrency: string;
  createdAt: string;
  lastLogin: string | null;
  workspaceMembers: Array<{
    workspace: { id: string; name: string; type: string; createdAt: string; _count: { items: number; members: number } };
    role: string;
  }>;
  _count: { items: number; comments: number; favorites: number; notifications: number; reminders: number; notes: number };
}

const SuperAdmin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterVerified, setFilterVerified] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeView, setActiveView] = useState<'dashboard' | 'users'>('dashboard');

  const showMsg = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { page, limit: 30 };
      if (search) params.search = search;
      if (filterRole) params.role = filterRole;
      if (filterVerified) params.verified = filterVerified;

      const response = await superAdminAPI.getUsers(params);
      setUsers(response.data.users);
      setTotalPages(response.data.pagination.totalPages);
      setTotal(response.data.pagination.total);
    } catch (error: any) {
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        showMsg('error', 'Failed to fetch users');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, filterRole, filterVerified]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await superAdminAPI.getStats();
      setStats(response.data);
    } catch {
      // stats are optional
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers, fetchStats]);

  const handleViewUser = async (userId: string) => {
    try {
      setActionLoading(userId);
      const response = await superAdminAPI.getUserDetail(userId);
      setSelectedUser(response.data.user);
    } catch {
      showMsg('error', 'Failed to load user details');
    } finally {
      setActionLoading(null);
    }
  };

  const handleImpersonate = async (userId: string) => {
    if (!window.confirm('⚠️ You are about to impersonate this user. This action is audit-logged. Continue?')) return;
    try {
      setActionLoading(userId);
      const response = await superAdminAPI.impersonateUser(userId);
      
      // Store the original admin credentials for returning
      const currentToken = localStorage.getItem('token');
      const currentUserData = localStorage.getItem('user');
      if (currentToken) localStorage.setItem('sa_original_token', currentToken);
      if (currentUserData) localStorage.setItem('sa_original_user', currentUserData);

      // Switch to impersonated user (server also sets new session cookie)
      dispatch(setCredentials({
        user: response.data.user,
        token: response.data.token,
      }));

      showMsg('success', `Now impersonating ${response.data.user.email}`);
      setTimeout(() => navigate('/'), 1000);
    } catch (error: any) {
      showMsg('error', error.response?.data?.error || 'Impersonation failed');
    } finally {
      setActionLoading(null);
    }
  };

  const handleVerifyEmail = async (userId: string) => {
    try {
      setActionLoading(userId);
      await superAdminAPI.verifyUserEmail(userId);
      showMsg('success', 'Email verified manually');
      fetchUsers();
    } catch {
      showMsg('error', 'Failed to verify email');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setActionLoading(userId);
      await superAdminAPI.deleteUser(userId);
      showMsg('success', 'User deleted successfully');
      setShowDeleteConfirm(null);
      setSelectedUser(null);
      fetchUsers();
      fetchStats();
    } catch (error: any) {
      showMsg('error', error.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      setActionLoading(userId);
      await superAdminAPI.updateUserRole(userId, newRole);
      showMsg('success', `Role updated to ${newRole}`);
      fetchUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser(prev => prev ? { ...prev, platformRole: newRole } : null);
      }
    } catch (error: any) {
      showMsg('error', error.response?.data?.error || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return 'Never';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleBackToApp = () => {
    navigate('/');
  };

  // ACCESS DENIED — this is a platform-scoped internal tool
  if (accessDenied) {
    return (
      <div className="sa-access-denied">
        <div className="sa-denied-card">
          <FontAwesomeIcon icon={faLock} className="sa-denied-icon" />
          <h2>Access Restricted</h2>
          <p>This is an internal platform administration console.</p>
          <p>You do not have the required permissions.</p>
          <button className="sa-btn-back" onClick={() => navigate('/')}>
            <FontAwesomeIcon icon={faArrowLeft} /> Return to App
          </button>
        </div>
      </div>
    );
  }

  // STANDALONE ADMIN CONSOLE — separate from customer UI
  return (
    <div className="sa-console">
      {/* Admin Console Top Bar */}
      <div className="sa-topbar">
        <div className="sa-topbar-left">
          <FontAwesomeIcon icon={faShieldAlt} className="sa-topbar-icon" />
          <span className="sa-topbar-title">Elvoaq Platform Admin</span>
          <span className="sa-topbar-env">INTERNAL</span>
        </div>
        <div className="sa-topbar-right">
          <span className="sa-topbar-user">{currentUser?.email}</span>
          <button className="sa-topbar-btn" onClick={handleBackToApp} title="Back to App">
            <FontAwesomeIcon icon={faArrowLeft} /> App
          </button>
          <button className="sa-topbar-btn logout" onClick={handleLogout} title="Logout">
            <FontAwesomeIcon icon={faSignOutAlt} />
          </button>
        </div>
      </div>

      <div className="sa-body">
      {/* Header */}
      <div className="sa-header">
        <div className="sa-header-left">
          <div className="sa-header-icon">
            <FontAwesomeIcon icon={faShieldAlt} />
          </div>
          <div>
            <h1>Super Admin Console</h1>
            <p>Platform-wide administration &amp; user management</p>
          </div>
        </div>
        <div className="sa-header-badge">
          <FontAwesomeIcon icon={faCrown} />
          PLATFORM ADMIN
        </div>
      </div>

      {/* Message Toast */}
      {message && (
        <div className={`sa-toast ${message.type}`}>
          <FontAwesomeIcon icon={message.type === 'success' ? faCheckCircle : faExclamationTriangle} />
          {message.text}
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="sa-tabs">
        <button className={`sa-tab ${activeView === 'dashboard' ? 'active' : ''}`} onClick={() => setActiveView('dashboard')}>
          <FontAwesomeIcon icon={faChartBar} /> Dashboard
        </button>
        <button className={`sa-tab ${activeView === 'users' ? 'active' : ''}`} onClick={() => setActiveView('users')}>
          <FontAwesomeIcon icon={faUsers} /> Users ({total})
        </button>
      </div>

      {/* Dashboard View */}
      {activeView === 'dashboard' && stats && (
        <div className="sa-dashboard">
          <div className="sa-stats-grid">
            <div className="sa-stat-card">
              <div className="sa-stat-icon users"><FontAwesomeIcon icon={faUsers} /></div>
              <div className="sa-stat-value">{stats.totalUsers}</div>
              <div className="sa-stat-label">Total Users</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-icon verified"><FontAwesomeIcon icon={faUserCheck} /></div>
              <div className="sa-stat-value">{stats.verifiedUsers}</div>
              <div className="sa-stat-label">Verified</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-icon unverified"><FontAwesomeIcon icon={faTimesCircle} /></div>
              <div className="sa-stat-value">{stats.unverifiedUsers}</div>
              <div className="sa-stat-label">Unverified</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-icon workspaces"><FontAwesomeIcon icon={faDatabase} /></div>
              <div className="sa-stat-value">{stats.totalWorkspaces}</div>
              <div className="sa-stat-label">Workspaces</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-icon items"><FontAwesomeIcon icon={faServer} /></div>
              <div className="sa-stat-value">{stats.totalItems}</div>
              <div className="sa-stat-label">Total Items</div>
            </div>
            <div className="sa-stat-card">
              <div className="sa-stat-icon recent"><FontAwesomeIcon icon={faUsersCog} /></div>
              <div className="sa-stat-value">{stats.recentSignups}</div>
              <div className="sa-stat-label">New (7 days)</div>
            </div>
          </div>
        </div>
      )}

      {/* Users View */}
      {activeView === 'users' && (
        <div className="sa-users-section">
          {/* Selected User Detail Panel */}
          {selectedUser && (
            <div className="sa-user-detail-overlay">
              <div className="sa-user-detail-panel">
                <div className="sa-detail-header">
                  <h3>
                    <FontAwesomeIcon icon={faEye} /> User Details
                  </h3>
                  <button className="sa-close-btn" onClick={() => { setSelectedUser(null); setShowDeleteConfirm(null); }}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>

                <div className="sa-detail-body">
                  <div className="sa-detail-info">
                    <div className="sa-detail-avatar">
                      {(selectedUser.firstName || 'U')[0].toUpperCase()}
                    </div>
                    <div>
                      <h4>{selectedUser.firstName} {selectedUser.lastName || ''}</h4>
                      <p className="sa-detail-email">{selectedUser.email}</p>
                      <div className="sa-detail-badges">
                        <span className={`sa-badge ${selectedUser.platformRole === 'PLATFORM_ADMIN' ? 'admin' : 'user'}`}>
                          {selectedUser.platformRole}
                        </span>
                        <span className={`sa-badge ${selectedUser.emailVerified ? 'verified' : 'unverified'}`}>
                          <FontAwesomeIcon icon={selectedUser.emailVerified ? faCheckCircle : faTimesCircle} />
                          {selectedUser.emailVerified ? 'Verified' : 'Unverified'}
                        </span>
                        {selectedUser.twoFactorEnabled && (
                          <span className="sa-badge twofa">2FA</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="sa-detail-stats">
                    <div className="sa-mini-stat"><span>{selectedUser._count.items}</span>Items</div>
                    <div className="sa-mini-stat"><span>{selectedUser._count.comments}</span>Comments</div>
                    <div className="sa-mini-stat"><span>{selectedUser._count.favorites}</span>Favorites</div>
                    <div className="sa-mini-stat"><span>{selectedUser._count.reminders}</span>Reminders</div>
                  </div>

                  <div className="sa-detail-meta">
                    <p><strong>Joined:</strong> {formatDate(selectedUser.createdAt)}</p>
                    <p><strong>Last Login:</strong> {formatDate(selectedUser.lastLogin)}</p>
                    <p><strong>Currency:</strong> {selectedUser.defaultCurrency}</p>
                    {selectedUser.bio && <p><strong>Bio:</strong> {selectedUser.bio}</p>}
                  </div>

                  {selectedUser.workspaceMembers.length > 0 && (
                    <div className="sa-detail-workspaces">
                      <h5>Workspaces ({selectedUser.workspaceMembers.length})</h5>
                      {selectedUser.workspaceMembers.map((wm) => (
                        <div key={wm.workspace.id} className="sa-workspace-card">
                          <span className="sa-ws-name">{wm.workspace.name}</span>
                          <span className="sa-ws-role">{wm.role}</span>
                          <span className="sa-ws-stats">{wm.workspace._count.items} items · {wm.workspace._count.members} members</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="sa-detail-actions">
                    {!selectedUser.emailVerified && (
                      <button className="sa-action-btn verify" onClick={() => handleVerifyEmail(selectedUser.id)}
                        disabled={actionLoading === selectedUser.id}>
                        <FontAwesomeIcon icon={faUserCheck} /> Verify Email
                      </button>
                    )}
                    <button className="sa-action-btn impersonate" onClick={() => handleImpersonate(selectedUser.id)}
                      disabled={actionLoading === selectedUser.id || selectedUser.platformRole === 'PLATFORM_ADMIN'}>
                      <FontAwesomeIcon icon={faUserSecret} /> Impersonate
                    </button>
                    <select
                      className="sa-role-select"
                      value={selectedUser.platformRole}
                      onChange={(e) => handleRoleChange(selectedUser.id, e.target.value)}
                      disabled={actionLoading === selectedUser.id || selectedUser.id === currentUser?.id}
                    >
                      <option value="PLATFORM_USER">PLATFORM_USER</option>
                      <option value="PLATFORM_ADMIN">PLATFORM_ADMIN</option>
                    </select>
                  </div>

                  {/* Danger Zone */}
                  <div className="sa-danger-zone">
                    {showDeleteConfirm === selectedUser.id ? (
                      <div className="sa-delete-confirm">
                        <p>⚠️ <strong>This will permanently delete this user and all their data.</strong></p>
                        <div className="sa-delete-actions">
                          <button className="sa-action-btn cancel" onClick={() => setShowDeleteConfirm(null)}>Cancel</button>
                          <button className="sa-action-btn delete" onClick={() => handleDeleteUser(selectedUser.id)}
                            disabled={actionLoading === selectedUser.id}>
                            <FontAwesomeIcon icon={actionLoading === selectedUser.id ? faSpinner : faTrash}
                              spin={actionLoading === selectedUser.id} />
                            Confirm Delete
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button className="sa-action-btn delete-trigger"
                        onClick={() => setShowDeleteConfirm(selectedUser.id)}
                        disabled={selectedUser.platformRole === 'PLATFORM_ADMIN'}>
                        <FontAwesomeIcon icon={faTrash} /> Delete User & All Data
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters Bar */}
          <div className="sa-filters">
            <div className="sa-search-box">
              <FontAwesomeIcon icon={faSearch} className="sa-search-icon" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select value={filterRole} onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}>
              <option value="">All Roles</option>
              <option value="PLATFORM_USER">Platform User</option>
              <option value="PLATFORM_ADMIN">Platform Admin</option>
            </select>
            <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setPage(1); }}>
              <option value="">All Status</option>
              <option value="true">Verified</option>
              <option value="false">Unverified</option>
            </select>
          </div>

          {/* Users Table */}
          {loading ? (
            <div className="sa-loading">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading users...
            </div>
          ) : (
            <>
              <div className="sa-table-wrapper">
                <table className="sa-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Items</th>
                      <th>Workspaces</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className={u.platformRole === 'PLATFORM_ADMIN' ? 'sa-admin-row' : ''}>
                        <td>
                          <div className="sa-user-cell">
                            <div className="sa-avatar">{(u.firstName || u.email[0]).charAt(0).toUpperCase()}</div>
                            <div>
                              <div className="sa-user-name">{u.firstName} {u.lastName || ''}</div>
                              <div className="sa-user-email">{u.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`sa-role-badge ${u.platformRole === 'PLATFORM_ADMIN' ? 'admin' : 'user'}`}>
                            {u.platformRole === 'PLATFORM_ADMIN' && <FontAwesomeIcon icon={faCrown} />}
                            {u.platformRole === 'PLATFORM_ADMIN' ? 'Admin' : 'User'}
                          </span>
                        </td>
                        <td>
                          <span className={`sa-status ${u.emailVerified ? 'verified' : 'unverified'}`}>
                            <FontAwesomeIcon icon={u.emailVerified ? faCheckCircle : faTimesCircle} />
                            {u.emailVerified ? 'Verified' : 'Unverified'}
                          </span>
                        </td>
                        <td className="sa-num">{u._count.items}</td>
                        <td className="sa-num">{u._count.workspaceMembers}</td>
                        <td className="sa-date">{formatDate(u.lastLogin)}</td>
                        <td>
                          <div className="sa-actions">
                            <button className="sa-icon-btn view" onClick={() => handleViewUser(u.id)}
                              disabled={actionLoading === u.id} title="View Details">
                              {actionLoading === u.id ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faEye} />}
                            </button>
                            <button className="sa-icon-btn impersonate" onClick={() => handleImpersonate(u.id)}
                              disabled={actionLoading === u.id || u.platformRole === 'PLATFORM_ADMIN'} title="Impersonate">
                              <FontAwesomeIcon icon={faUserSecret} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="sa-pagination">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <FontAwesomeIcon icon={faArrowLeft} /> Prev
                  </button>
                  <span>Page {page} of {totalPages}</span>
                  <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>{/* end sa-body */}
    </div>
  );
};

export default SuperAdmin;
